import React, { createContext, useCallback, useContext, useState } from 'react';

export function useStepper<T extends Record<string, any>>(max: number) {
  const [currentPage, setPage] = useState(0);
  const [data, setData] = useState<Partial<T>>();

  const handleNext = useCallback(() => {
    if (currentPage + 1 < max) {
      setPage((current) => current + 1);
    }
  }, [currentPage, max]);

  const handlePrev = useCallback(() => {
    if (currentPage - 1 >= 0) {
      setPage((current) => current - 1);
    }
  }, [currentPage]);

  const changePage = useCallback(
    (page: number) => {
      if (page < max && page >= 0) {
        setPage(page);
      }
    },
    [max]
  );

  const commit = useCallback(
    (formData: Partial<T>) => {
      const newValue = { ...(data ?? {}), ...formData };

      setData(newValue);

      return newValue;
    },
    [data]
  );

  const reset = useCallback(() => {
    setData({});

    return {};
  }, []);

  return {
    currentPage,
    handleNext,
    handlePrev,
    changePage,
    data,
    commit,
    reset,
  };
}

type StepperState = ReturnType<typeof useStepper>;

const StepperContext = createContext<StepperState>({
  data: {},
  currentPage: 0,
  handleNext: () => {},
  handlePrev: () => {},
  changePage: (page: number) => {},
  commit: (formData: any) => formData,
  reset: () => ({}),
});

type StepperProviderProps = StepperState & {
  children?: React.ReactNode;
};

export function StepperProvider({ children, ...rest }: StepperProviderProps) {
  return (
    <StepperContext.Provider value={rest}>{children}</StepperContext.Provider>
  );
}

export function useStepperContext<T extends Record<string, any>>() {
  return useContext(StepperContext) as Omit<StepperState, 'data'> & {
    data: Partial<T>;
    commit: (data: Partial<T>) => Partial<T>;
  };
}
